import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { PermissionGate, useRBAC } from '@/contexts/RBACContext';
import { formatDate, formatRelativeTime, formatDateTime } from '@/utils/localeFormatters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import EmailComposer from '@/components/EmailComposer';
import { 
  Plus, Mail, Phone, Building, Sparkles, TrendingUp, TrendingDown, 
  ChevronRight, Clock, Search, Filter, SortAsc, SortDesc, Calendar,
  X, RefreshCw, Users, LayoutGrid, List, ExternalLink, Kanban,
  Edit, PhoneCall, MoreHorizontal, UserCircle, CheckSquare, CalendarDays,
  Trash2, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, MessageCircle, Download,
  Command, MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status badge styles following design system
const statusStyles = {
  new: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  lead: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  contacted: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  qualified: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  opportunity: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  customer: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  meeting_scheduled: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  meeting_cancelled: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  under_qualification: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  not_interested: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  lost: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
};

// Priority indicator colors
const priorityColors = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  normal: 'bg-emerald-500',
};

const Contacts = () => {
  const { token, user } = useAuth();
  const { t, currentLanguage, translations } = useLanguage();
  const { hasPermission } = useRBAC();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ sources: [], statuses: [], owners: [] });
  
  // Flag to track if URL params have been applied
  const [urlParamsApplied, setUrlParamsApplied] = useState(false);

  // Lead status stages
  const LEAD_STAGES = useMemo(() => [
    { id: 'new', label: t('contacts.newLead', 'New Lead'), color: '#3b82f6' },
    { id: 'lead', label: 'Lead', color: '#6366f1' },
    { id: 'contacted', label: t('contacts.contacted', 'Contacted'), color: '#8b5cf6' },
    { id: 'qualified', label: t('contacts.qualified', 'Qualified'), color: '#f59e0b' },
    { id: 'opportunity', label: t('contacts.opportunity', 'Opportunity'), color: '#f97316' },
    { id: 'customer', label: t('contacts.customer', 'Customer'), color: '#22c55e' },
    { id: 'meeting_scheduled', label: 'Meeting Scheduled', color: '#0891b2' },
    { id: 'meeting_cancelled', label: 'Meeting Cancelled', color: '#dc2626' },
    { id: 'under_qualification', label: 'Under Qualification', color: '#a855f7' },
    { id: 'not_interested', label: 'Not Interested', color: '#6b7280' },
    { id: 'lost', label: 'Lost', color: '#ef4444' },
  ], [t]);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [lifecycleStageFilter, setLifecycleStageFilter] = useState('all');
  const [lifecycleStages, setLifecycleStages] = useState([]);
  const [lifecycleSearchQuery, setLifecycleSearchQuery] = useState('');
  const [ownershipTab, setOwnershipTab] = useState('my');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [assignedFrom, setAssignedFrom] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [lastActivityFrom, setLastActivityFrom] = useState('');
  const [lastActivityTo, setLastActivityTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [teamFilter, setTeamFilter] = useState('all');
  const [teams, setTeams] = useState([]);
  const [quickDateFilter, setQuickDateFilter] = useState('all');
  
  // Quick date filter options
  const quickDateOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
  ];
  
  // Helper to format date as YYYY-MM-DD without timezone conversion
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Helper to calculate date range based on quick filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case 'today':
        return { from: formatDateLocal(today), to: formatDateLocal(today) };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: formatDateLocal(yesterday), to: formatDateLocal(yesterday) };
      case 'this_week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return { from: formatDateLocal(startOfWeek), to: formatDateLocal(today) };
      case 'last_week':
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        return { from: formatDateLocal(lastWeekStart), to: formatDateLocal(lastWeekEnd) };
      case 'this_month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: formatDateLocal(startOfMonth), to: formatDateLocal(today) };
      case 'last_month':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        return { from: formatDateLocal(lastMonthStart), to: formatDateLocal(lastMonthEnd) };
      case 'this_year':
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        return { from: formatDateLocal(startOfYear), to: formatDateLocal(today) };
      default:
        return { from: '', to: '' };
    }
  };
  
  // Handle quick date filter change
  const handleQuickDateChange = (value) => {
    setQuickDateFilter(value);
    if (value !== 'custom' && value !== 'all') {
      const range = getDateRange(value);
      setDateFrom(range.from);
      setDateTo(range.to);
    } else if (value === 'all') {
      setDateFrom('');
      setDateTo('');
    }
  };
  
  // Quick action states
  const [emailContact, setEmailContact] = useState(null);
  const [isEmailComposerOpen, setIsEmailComposerOpen] = useState(false);
  
  // Multi-select and delete states
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Bulk owner change states
  const [isBulkOwnerDialogOpen, setIsBulkOwnerDialogOpen] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState('');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pageInput, setPageInput] = useState('1');

  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+971',
    company: '',
    position: '',
    lead_score: 50,
    status: 'new',
    source: 'direct',
  });

  // Extract unique contact owners for filter
  const contactOwners = useMemo(() => {
    const owners = new Map();
    contacts.forEach(c => {
      const owner = c.owner || c.assignee;
      if (owner?.id) {
        owners.set(owner.id, { id: owner.id, name: owner.full_name || owner.email || 'Unknown' });
      }
    });
    return Array.from(owners.values());
  }, [contacts]);

  // Group contacts by status for board view
  const contactsByStatus = useMemo(() => {
    const grouped = {};
    LEAD_STAGES.forEach(stage => grouped[stage.id] = []);
    contacts.forEach(contact => {
      const status = contact.status || 'new';
      if (grouped[status]) grouped[status].push(contact);
      else grouped['new'].push(contact);
    });
    return grouped;
  }, [contacts, LEAD_STAGES]);

  // Check for active filters
  const hasActiveFilters = statusFilter !== 'all' || sourceFilter !== 'all' || 
    ownerFilter !== 'all' || teamFilter !== 'all' || lifecycleStageFilter !== 'all' || 
    dateFrom || dateTo || assignedFrom || assignedTo || lastActivityFrom || lastActivityTo || searchQuery;

  // Apply URL parameters as filters (for drilldown from reports)
  useEffect(() => {
    if (urlParamsApplied) return; // Only apply once
    
    const urlOwnerName = searchParams.get('owner_name');
    const urlOwnerId = searchParams.get('owner_id');
    const urlFromDate = searchParams.get('from_date');
    const urlToDate = searchParams.get('to_date');
    const urlLifecycleStage = searchParams.get('lifecycle_stage');
    const urlSource = searchParams.get('source');
    const urlResponded = searchParams.get('responded');
    const urlStatus = searchParams.get('status');
    
    let hasUrlParams = false;
    
    if (urlOwnerId && urlOwnerId !== 'all') {
      setOwnerFilter(urlOwnerId);
      setOwnershipTab('all'); // Show all contacts, not just 'my'
      hasUrlParams = true;
    }
    
    if (urlFromDate) {
      setDateFrom(urlFromDate);
      hasUrlParams = true;
    }
    
    if (urlToDate) {
      setDateTo(urlToDate);
      hasUrlParams = true;
    }
    
    if (urlLifecycleStage && urlLifecycleStage !== 'all') {
      setLifecycleStageFilter(urlLifecycleStage);
      hasUrlParams = true;
    }
    
    if (urlSource && urlSource !== 'all') {
      setSourceFilter(urlSource);
      hasUrlParams = true;
    }
    
    if (urlStatus && urlStatus !== 'all') {
      setStatusFilter(urlStatus);
      hasUrlParams = true;
    }
    
    if (hasUrlParams) {
      setShowFilters(true); // Show filter panel when coming from report drilldown
    }
    
    setUrlParamsApplied(true);
  }, [searchParams, urlParamsApplied]);

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        token,  // Token must be passed as query param for backend auth
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (ownerFilter !== 'all') params.append('owner_id', ownerFilter);
      if (teamFilter !== 'all') params.append('team', teamFilter);
      if (lifecycleStageFilter !== 'all') params.append('lifecycle_stage', lifecycleStageFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (assignedFrom) params.append('assigned_from', assignedFrom);
      if (assignedTo) params.append('assigned_to', assignedTo);
      if (lastActivityFrom) params.append('last_activity_from', lastActivityFrom);
      if (lastActivityTo) params.append('last_activity_to', lastActivityTo);

      // Different endpoint based on tab - for super_admin, don't send ownership filter to see all contacts
      let endpoint = `${API}/contacts`;
      // Only add ownership_filter for non-super_admin users, or when specifically requested
      if (ownershipTab === 'my') {
        params.append('ownership_filter', 'my');
      } else if (ownershipTab === 'all') {
        params.append('ownership_filter', 'all');
      } else if (ownershipTab === 'all_my') {
        params.append('ownership_filter', 'all_my');
      }
      // Note: 'all_contacts' tab won't add any ownership_filter, showing ALL contacts for admins

      const response = await axios.get(`${endpoint}?${params}`);

      if (response.data.contacts) {
        setContacts(response.data.contacts);
        setTotalContacts(response.data.total || response.data.contacts.length);
        setTotalPages(response.data.pages || Math.ceil((response.data.total || response.data.contacts.length) / pageSize));
      } else if (Array.isArray(response.data)) {
        setContacts(response.data);
        setTotalContacts(response.data.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/contacts/filters?token=${token}`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch teams
  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams?token=${token}`);
      // API returns {success: true, teams: [...]} so extract the teams array
      setTeams(response.data?.teams || response.data || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setTeams([]);
    }
  };

  // Fetch all users for bulk owner change
  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/accounts/users?token=${token}`);
      // API returns {success: true, users: [...]} so extract the users array
      setAllUsers(response.data?.users || response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setAllUsers([]);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchFilterOptions();
    fetchTeams();
    fetchAllUsers();
    fetchLifecycleStages();
  }, [token, currentPage, pageSize, sortBy, sortOrder, ownershipTab]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setCurrentPage(1);
      fetchContacts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, statusFilter, sourceFilter, ownerFilter, teamFilter, lifecycleStageFilter, dateFrom, dateTo, assignedFrom, assignedTo, lastActivityFrom, lastActivityTo]);

  // Fetch lifecycle stages from master data
  const fetchLifecycleStages = async () => {
    try {
      const response = await axios.get(`${API}/master/lifecycle-stages?token=${token}`);
      if (response.data.success && response.data.stages) {
        setLifecycleStages(response.data.stages);
      }
    } catch (error) {
      console.error('Failed to fetch lifecycle stages:', error);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setSourceFilter('all');
    setOwnerFilter('all');
    setTeamFilter('all');
    setLifecycleStageFilter('all');
    setLifecycleSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setAssignedFrom('');
    setAssignedTo('');
    setLastActivityFrom('');
    setLastActivityTo('');
    setQuickDateFilter('all');
    setCurrentPage(1);
  };

  // Sort handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Selection handlers
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  // Quick actions
  const handleQuickCall = (contact, e) => {
    e.stopPropagation();
    if (contact.phone) {
      window.open(`tel:${contact.phone}`, '_self');
    } else {
      toast.error('No phone number available');
    }
  };

  const handleQuickEmail = (contact, e) => {
    e.stopPropagation();
    setEmailContact(contact);
    setIsEmailComposerOpen(true);
  };

  const handleQuickWhatsApp = (contact, e) => {
    e.stopPropagation();
    if (contact.phone) {
      navigate(`/whatsapp?contact=${contact.id}&phone=${encodeURIComponent(contact.phone)}`);
    } else {
      toast.error('No phone number available');
    }
  };

  // Handle contact view - mark as viewed for New Leads tracking
  const handleViewContact = async (contact) => {
    // Mark contact as viewed in the background
    try {
      await axios.post(`${API}/contacts/${contact.id}/mark-viewed?token=${token}`);
    } catch (error) {
      // Don't block navigation if marking fails
      console.error('Error marking contact as viewed:', error);
    }
    navigate(`/contacts/${contact.id}`);
  };

  // Delete handlers
  const openDeleteDialog = (contact, e) => {
    e?.stopPropagation();
    setContactToDelete(contact);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      setIsDeleting(true);
      await axios.delete(`${API}/contacts/${contactId}?token=${token}`);
      toast.success('Contact deleted');
      setIsDeleteDialogOpen(false);
      setContactToDelete(null);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsDeleting(true);
      await Promise.all(
        selectedContacts.map(id => 
          axios.delete(`${API}/contacts/${id}?token=${token}`)
        )
      );
      toast.success(`${selectedContacts.length} contacts deleted`);
      setSelectedContacts([]);
      setIsDeleteDialogOpen(false);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete some contacts');
    } finally {
      setIsDeleting(false);
    }
  };

  // Bulk owner change
  const handleBulkOwnerChange = async () => {
    if (!selectedNewOwner) return;
    try {
      setIsBulkUpdating(true);
      await Promise.all(
        selectedContacts.map(id =>
          axios.patch(`${API}/contacts/${id}?token=${token}`, { owner_id: selectedNewOwner })
        )
      );
      toast.success(`Owner updated for ${selectedContacts.length} contacts`);
      setSelectedContacts([]);
      setIsBulkOwnerDialogOpen(false);
      setSelectedNewOwner('');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update owner');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // Export to Excel
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const response = await axios.get(`${API}/contacts/export?token=${token}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contacts_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export completed');
    } catch (error) {
      toast.error('Failed to export contacts');
    } finally {
      setIsExporting(false);
    }
  };

  // Add contact
  const handleAddContact = async () => {
    try {
      const contactData = {
        ...newContact,
        phone: newContact.phone ? `${newContact.countryCode}${newContact.phone.replace(/^0+/, '')}` : '',
        name: newContact.name || `${newContact.email?.split('@')[0] || 'Contact'}`,
      };
      await axios.post(`${API}/contacts?token=${token}`, contactData);
      toast.success('Contact created');
      setIsAddDialogOpen(false);
      setNewContact({
        name: '', email: '', phone: '', countryCode: '+971',
        company: '', position: '', lead_score: 50, status: 'new', source: 'direct',
      });
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contact');
    }
  };

  // Format helpers
  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const getInitials = (name, email) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return '?';
  };

  const getStatusStyle = (status) => {
    return statusStyles[status] || statusStyles.new;
  };

  const getPriorityFromScore = (score) => {
    if (score >= 80) return 'high';
    if (score >= 50) return 'medium';
    return 'normal';
  };

  // Loading state
  if (loading && contacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="contacts-loading">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="animate-spin text-slate-400" size={32} />
          <span className="text-slate-500 text-sm">Loading contacts...</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contacts-page" className="space-y-6 p-6 md:p-8 max-w-[1600px] mx-auto">
      {/* Header Section */}
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            {t('contacts.title', 'Contacts')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalContacts.toLocaleString()} {totalContacts === 1 ? 'contact' : 'contacts'}
            {hasActiveFilters && <span className="text-slate-400"> (filtered)</span>}
          </p>
        </div>
        
        {/* Add Contact Button */}
        <PermissionGate permission="CRM.CONTACTS" action="create">
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-slate-900 hover:bg-slate-800 text-white gap-2 rounded-lg shadow-none"
                data-testid="add-contact-button"
              >
                <Plus size={16} />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg bg-white">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">Create New Contact</DialogTitle>
                <DialogDescription className="text-slate-500">
                  Add a new contact to your CRM
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      className="h-10"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      className="h-10"
                      data-testid="input-email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Country</Label>
                    <Select value={newContact.countryCode} onValueChange={(v) => setNewContact({ ...newContact, countryCode: v })}>
                      <SelectTrigger className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="+971">UAE (+971)</SelectItem>
                        <SelectItem value="+966">KSA (+966)</SelectItem>
                        <SelectItem value="+91">India (+91)</SelectItem>
                        <SelectItem value="+1">USA (+1)</SelectItem>
                        <SelectItem value="+44">UK (+44)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">Phone</Label>
                    <Input
                      id="phone"
                      placeholder="50 123 4567"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      className="h-10"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-sm font-medium text-slate-700">Company</Label>
                    <Input
                      id="company"
                      placeholder="Acme Inc"
                      value={newContact.company}
                      onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                      className="h-10"
                      data-testid="input-company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source" className="text-sm font-medium text-slate-700">Source</Label>
                    <Select value={newContact.source} onValueChange={(v) => setNewContact({ ...newContact, source: v })}>
                      <SelectTrigger className="h-10" data-testid="select-source">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct">Direct</SelectItem>
                        <SelectItem value="website">Website</SelectItem>
                        <SelectItem value="referral">Referral</SelectItem>
                        <SelectItem value="social">Social Media</SelectItem>
                        <SelectItem value="hubspot">HubSpot</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddContact}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="create-contact-btn"
                >
                  Create Contact
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </PermissionGate>
      </div>

      {/* Tabs */}
      <Tabs value={ownershipTab} onValueChange={(v) => { setOwnershipTab(v); setCurrentPage(1); setSelectedContacts([]); }}>
        <TabsList className="bg-transparent border-b border-slate-200 w-full justify-start rounded-none h-auto p-0 gap-0">
          <TabsTrigger 
            value="my" 
            className="data-[state=active]:border-b-2 data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none px-4 py-3 text-sm font-medium text-slate-500 data-[state=active]:text-slate-900 hover:text-slate-700 transition-colors"
            data-testid="tab-my-actionable"
          >
            My Actionable
          </TabsTrigger>
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:border-b-2 data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none px-4 py-3 text-sm font-medium text-slate-500 data-[state=active]:text-slate-900 hover:text-slate-700 transition-colors"
            data-testid="tab-my-leads"
          >
            My Leads
          </TabsTrigger>
          <TabsTrigger 
            value="all_my" 
            className="data-[state=active]:border-b-2 data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none px-4 py-3 text-sm font-medium text-slate-500 data-[state=active]:text-slate-900 hover:text-slate-700 transition-colors"
            data-testid="tab-all-my-leads"
          >
            All Leads
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search and Filter Bar */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-12 h-10 bg-white border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/10"
            data-testid="contact-search-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 text-xs text-slate-400">
            <Command size={12} />
            <span>K</span>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* View Toggle */}
          <div className="flex border border-slate-200 rounded-lg bg-white p-0.5">
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 px-3 rounded-md ${viewMode === 'list' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setViewMode('list')}
              data-testid="view-list-btn"
            >
              <List size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 px-3 rounded-md ${viewMode === 'card' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setViewMode('card')}
              data-testid="view-card-btn"
            >
              <LayoutGrid size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 px-3 rounded-md ${viewMode === 'board' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setViewMode('board')}
              data-testid="view-board-btn"
            >
              <Kanban size={14} />
            </Button>
          </div>

          {/* Quick Filters */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px] h-9 bg-white border-slate-200 text-sm" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {LEAD_STAGES.map(stage => (
                <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[120px] h-9 bg-white border-slate-200 text-sm" data-testid="source-filter">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {filterOptions.sources.map(source => (
                <SelectItem key={source} value={source}>{source}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-9 gap-1.5 ${showFilters ? 'bg-slate-900 text-white' : 'border-slate-200'}`}
            data-testid="toggle-filters-btn"
          >
            <Filter size={14} />
            Filters
            {hasActiveFilters && !showFilters && (
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            )}
          </Button>

          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearFilters} 
              className="h-9 text-slate-500 hover:text-slate-700"
              data-testid="clear-filters-btn"
            >
              <X size={14} className="mr-1" />
              Clear
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportToExcel}
            disabled={isExporting || totalContacts === 0}
            className="h-9 gap-1.5 border-slate-200 ml-auto"
            data-testid="export-contacts-btn"
          >
            {isExporting ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Export
          </Button>
        </div>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <Card className="p-4 bg-slate-50/50 border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Owner</Label>
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger className="h-9 bg-white" data-testid="owner-filter">
                  <SelectValue placeholder="All owners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All owners</SelectItem>
                  {contactOwners.map(owner => (
                    <SelectItem key={owner.id} value={owner.id}>{owner.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Team</Label>
              <Select value={teamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger className="h-9 bg-white" data-testid="team-filter">
                  <SelectValue placeholder="All teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All teams</SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Lifecycle Stage</Label>
              <Select value={lifecycleStageFilter} onValueChange={setLifecycleStageFilter}>
                <SelectTrigger className="h-9 bg-white" data-testid="lifecycle-stage-filter">
                  <SelectValue placeholder="All stages" />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search stages..."
                      value={lifecycleSearchQuery}
                      onChange={(e) => setLifecycleSearchQuery(e.target.value)}
                      className="h-8 text-sm"
                      data-testid="lifecycle-search-input"
                    />
                  </div>
                  <SelectItem value="all">All stages</SelectItem>
                  {lifecycleStages
                    .filter(stage => 
                      !lifecycleSearchQuery || 
                      (stage.display_name || stage.name || '').toLowerCase().includes(lifecycleSearchQuery.toLowerCase())
                    )
                    .map(stage => (
                      <SelectItem key={stage.id || stage.name} value={stage.name}>
                        {stage.display_name || stage.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Quick Date Filter</Label>
              <Select value={quickDateFilter} onValueChange={handleQuickDateChange}>
                <SelectTrigger className="h-9 bg-white" data-testid="quick-date-filter">
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  {quickDateOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Lead Created From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setQuickDateFilter('custom'); }}
                className="h-9 bg-white"
                data-testid="date-from-filter"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Lead Created To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setQuickDateFilter('custom'); }}
                className="h-9 bg-white"
                data-testid="date-to-filter"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Lead Assigned From</Label>
              <Input
                type="date"
                value={assignedFrom}
                onChange={(e) => setAssignedFrom(e.target.value)}
                className="h-9 bg-white"
                data-testid="assigned-from-filter"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Lead Assigned To</Label>
              <Input
                type="date"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="h-9 bg-white"
                data-testid="assigned-to-filter"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Sort by</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-9 bg-white" data-testid="sort-by-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Created Date</SelectItem>
                  <SelectItem value="assigned_at">Assigned Date</SelectItem>
                  <SelectItem value="updated_at">Last Updated</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="lead_score">Lead Score</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Order</Label>
              <Button
                variant="outline"
                className="w-full h-9 justify-between bg-white"
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                data-testid="sort-order-btn"
              >
                {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
                {sortOrder === 'desc' ? <SortDesc size={14} /> : <SortAsc size={14} />}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Main Content */}
      {viewMode === 'list' ? (
        /* List/Table View */
        <Card className="overflow-hidden border-slate-200 shadow-none" data-testid="contacts-list-view">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 backdrop-blur-sm sticky top-0 z-10 border-b border-slate-200">
                  <TableHead className="w-12 py-3 px-4">
                    <Checkbox
                      checked={selectedContacts.length === contacts.length && contacts.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead 
                    className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors"
                    onClick={() => handleSort('name')}
                    data-testid="sort-name"
                  >
                    <div className="flex items-center gap-1.5">
                      Name
                      {sortBy === 'name' ? (
                        sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : <ArrowUpDown size={12} className="opacity-30" />}
                    </div>
                  </TableHead>
                  
                  {/* Conditional columns based on tab */}
                  {ownershipTab === 'my' ? (
                    /* Actionable Leads columns */
                    <>
                      <TableHead 
                        className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center gap-1">
                          Status
                          {sortBy === 'status' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors"
                        onClick={() => handleSort('last_action_at')}
                      >
                        <div className="flex items-center gap-1">
                          Last Action
                          {sortBy === 'last_action_at' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
                        </div>
                      </TableHead>
                      <TableHead className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Last Action Time
                      </TableHead>
                      <TableHead 
                        className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors"
                        onClick={() => handleSort('next_action_due')}
                      >
                        <div className="flex items-center gap-1">
                          Next Action
                          {sortBy === 'next_action_due' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
                        </div>
                      </TableHead>
                      <TableHead className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Due
                      </TableHead>
                      <TableHead className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Reason
                      </TableHead>
                      <TableHead className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Priority
                      </TableHead>
                    </>
                  ) : (
                    /* My Leads (all) and All My Leads columns */
                    <>
                      <TableHead 
                        className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors"
                        onClick={() => handleSort('email')}
                        data-testid="sort-email"
                      >
                        <div className="flex items-center gap-1">
                          Email
                          {sortBy === 'email' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
                        </div>
                      </TableHead>
                      <TableHead className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Phone
                      </TableHead>
                      <TableHead className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Owner
                      </TableHead>
                      <TableHead 
                        className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors"
                        onClick={() => handleSort('updated_at')}
                        data-testid="sort-updated"
                      >
                        <div className="flex items-center gap-1">
                          Last Updated
                          {sortBy === 'updated_at' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors"
                        onClick={() => handleSort('created_at')}
                        data-testid="sort-created"
                      >
                        <div className="flex items-center gap-1">
                          Created
                          {sortBy === 'created_at' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors"
                        onClick={() => handleSort('status')}
                        data-testid="sort-status"
                      >
                        <div className="flex items-center gap-1">
                          Status
                          {sortBy === 'status' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors"
                        onClick={() => handleSort('company')}
                        data-testid="sort-company"
                      >
                        <div className="flex items-center gap-1">
                          Company
                          {sortBy === 'company' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
                        </div>
                      </TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => {
                  const isSelected = selectedContacts.includes(contact.id);
                  const ownerName = contact.owner?.full_name || contact.assignee?.full_name || 'Unassigned';
                  const statusStyle = getStatusStyle(contact.status);
                  const priority = getPriorityFromScore(contact.lead_score);
                  const nextActionOverdue = isOverdue(contact.next_action_due);

                  return (
                    <TableRow 
                      key={contact.id}
                      className={`group cursor-pointer border-b border-slate-100 transition-colors ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/50'} ${nextActionOverdue && ownershipTab === 'my' ? 'bg-red-50/50' : ''}`}
                      onClick={() => handleViewContact(contact)}
                      data-testid={`contact-row-${contact.id}`}
                    >
                      <TableCell className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleContactSelection(contact.id)}
                          data-testid={`list-checkbox-${contact.id}`}
                        />
                      </TableCell>
                      <TableCell className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {/* Priority indicator */}
                          <div className={`w-1 h-10 rounded-full ${priorityColors[priority]}`} />
                          
                          {/* Avatar */}
                          <Avatar className="h-9 w-9 border border-slate-200">
                            <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-medium">
                              {getInitials(contact.name, contact.email)}
                            </AvatarFallback>
                          </Avatar>
                          
                          {/* Name only - email shown in separate column for non-actionable tabs */}
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 text-sm truncate">
                              {contact.name || contact.email?.split('@')[0] || 'Unknown'}
                            </p>
                            {ownershipTab === 'my' && contact.email && (
                              <p className="text-xs text-slate-500 truncate">{contact.email}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      
                      {/* Conditional columns based on tab */}
                      {ownershipTab === 'my' ? (
                        /* Actionable Leads columns */
                        <>
                          <TableCell className="py-3 px-4">
                            <Badge className={`${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border} text-xs font-medium rounded-full`}>
                              {contact.status?.replace(/_/g, ' ') || 'new'}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <span className="text-sm text-slate-600 capitalize">
                              {contact.last_action_type?.replace(/_/g, ' ') || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <span className="text-xs text-slate-500 whitespace-nowrap">
                              {contact.last_action_at ? formatRelativeTime(contact.last_action_at) : '-'}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <span className="text-sm text-slate-700 font-medium">
                              {contact.next_action || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <span className={`text-xs whitespace-nowrap font-medium ${nextActionOverdue ? 'text-red-600' : 'text-slate-500'}`}>
                              {contact.next_action_due ? formatRelativeTime(contact.next_action_due) : '-'}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <span className="text-xs text-slate-500 max-w-[150px] truncate block" title={contact.action_reason}>
                              {contact.action_reason || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <Badge className={`text-xs ${nextActionOverdue ? 'bg-red-500 text-white' : contact.lead_score >= 80 ? 'bg-orange-500 text-white' : 'bg-blue-100 text-blue-700'}`}>
                              {nextActionOverdue ? 'Overdue' : contact.lead_score >= 80 ? 'High' : contact.lead_score >= 50 ? 'Medium' : 'Normal'}
                            </Badge>
                          </TableCell>
                        </>
                      ) : (
                        /* My Leads (all) and All My Leads columns */
                        <>
                          <TableCell className="py-3 px-4">
                            <span className="text-sm text-slate-600">
                              {contact.email || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm text-slate-600 font-mono">{contact.phone || '-'}</span>
                              {contact.phone && contact.has_whatsapp && (
                                <button 
                                  className="text-green-500 flex-shrink-0 hover:text-green-600 transition-colors"
                                  title="Send WhatsApp Message"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/whatsapp?contact=${contact.id}&phone=${encodeURIComponent(contact.phone)}`);
                                  }}
                                >
                                  <MessageSquare size={14} />
                                </button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-medium">
                                {ownerName[0]?.toUpperCase() || 'U'}
                              </div>
                              <span className="text-sm text-slate-600 truncate max-w-[100px]" title={ownerName}>
                                {ownerName}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <span className="text-xs text-slate-500 whitespace-nowrap">
                              {formatDateTime(contact.updated_at || contact.last_activity_at || contact.created_at)}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <span className="text-xs text-slate-500 whitespace-nowrap">
                              {formatDateTime(contact.created_at)}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <Badge className={`${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border} text-xs font-medium rounded-full`}>
                              {contact.status?.replace(/_/g, ' ') || 'new'}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <span className="text-sm text-slate-600">
                              {contact.company || '-'}
                            </span>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
                {contacts.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={ownershipTab === 'my' ? 9 : 9} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                          <Users size={24} className="text-slate-400" />
                        </div>
                        <div>
                          <p className="text-slate-600 font-medium">No contacts found</p>
                          <p className="text-sm text-slate-400">
                            {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first contact to get started'}
                          </p>
                        </div>
                        {hasActiveFilters && (
                          <Button variant="outline" size="sm" onClick={clearFilters}>
                            Clear Filters
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>
                  {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalContacts)} of {totalContacts.toLocaleString()}
                </span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[70px] h-8 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span>per page</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="h-8 bg-white"
                >
                  Previous
                </Button>
                <div className="px-3 text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8 bg-white"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      ) : viewMode === 'card' ? (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="contacts-card-view">
          {contacts.map((contact) => {
            const isSelected = selectedContacts.includes(contact.id);
            const ownerName = contact.owner?.full_name || contact.assignee?.full_name || 'Unassigned';
            const statusStyle = getStatusStyle(contact.status);
            const priority = getPriorityFromScore(contact.lead_score);

            return (
              <Card 
                key={contact.id}
                className={`p-4 cursor-pointer group transition-all hover:border-slate-300 ${isSelected ? 'ring-2 ring-slate-900' : ''}`}
                onClick={() => handleViewContact(contact)}
                data-testid={`contact-card-${contact.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="h-10 w-10 border border-slate-200">
                        <AvatarFallback className="bg-slate-100 text-slate-600 text-sm font-medium">
                          {getInitials(contact.name, contact.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${priorityColors[priority]}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate group-hover:text-slate-700">
                        {contact.name || contact.email?.split('@')[0] || 'Unknown'}
                      </p>
                      <p className="text-xs text-slate-500 truncate">{contact.email || 'No email'}</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleContactSelection(contact.id)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`checkbox-${contact.id}`}
                  />
                </div>

                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Phone size={12} />
                    <span className="font-mono">{contact.phone || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <UserCircle size={12} />
                    <span>{ownerName}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <Badge className={`${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border} text-xs rounded-full`}>
                    {contact.status?.replace(/_/g, ' ') || 'new'}
                  </Badge>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => handleQuickCall(contact, e)}>
                      <Phone size={12} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => handleQuickEmail(contact, e)}>
                      <Mail size={12} />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
          {contacts.length === 0 && !loading && (
            <div className="col-span-full text-center py-16">
              <Users size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No contacts found</p>
            </div>
          )}
        </div>
      ) : (
        /* Board View */
        <div className="overflow-x-auto pb-4" data-testid="contacts-board-view">
          <div className="flex gap-4 min-w-max">
            {LEAD_STAGES.slice(0, 6).map(stage => (
              <div key={stage.id} className="w-72 flex-shrink-0 bg-slate-50 rounded-xl border border-slate-200">
                <div className="p-3 border-b border-slate-200" style={{ borderTopColor: stage.color, borderTopWidth: '3px', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm text-slate-700">{stage.label}</span>
                    <Badge variant="outline" className="text-xs">{contactsByStatus[stage.id]?.length || 0}</Badge>
                  </div>
                </div>
                <div className="p-2 space-y-2 max-h-[calc(100vh-350px)] overflow-y-auto">
                  {contactsByStatus[stage.id]?.map(contact => (
                    <Card 
                      key={contact.id} 
                      className="p-3 cursor-pointer hover:border-slate-300 transition-colors"
                      onClick={() => handleViewContact(contact)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
                            {getInitials(contact.name, contact.email)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sm text-slate-800 truncate">
                          {contact.name || contact.email?.split('@')[0] || 'Unknown'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{contact.phone || contact.email || '-'}</p>
                    </Card>
                  ))}
                  {(!contactsByStatus[stage.id] || contactsByStatus[stage.id].length === 0) && (
                    <div className="text-center py-8 text-slate-400 text-xs">No contacts</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && contacts.length > 0 && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="flex items-center gap-2 bg-white px-4 py-3 rounded-lg shadow-lg border">
            <RefreshCw className="animate-spin text-slate-600" size={18} />
            <span className="text-sm text-slate-600">Updating...</span>
          </div>
        </div>
      )}

      {/* Email Composer */}
      <EmailComposer
        isOpen={isEmailComposerOpen}
        onClose={() => { setIsEmailComposerOpen(false); setEmailContact(null); }}
        contact={emailContact}
        onEmailSent={() => { toast.success('Email sent!'); fetchContacts(); }}
      />

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="delete-confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              {contactToDelete ? 'Delete Contact' : 'Delete Contacts'}
            </DialogTitle>
            <DialogDescription>
              {contactToDelete 
                ? `Delete "${contactToDelete.name || contactToDelete.email}"? This cannot be undone.`
                : `Delete ${selectedContacts.length} selected contacts? This cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => { setIsDeleteDialogOpen(false); setContactToDelete(null); }} disabled={isDeleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => contactToDelete ? handleDeleteContact(contactToDelete.id) : handleBulkDelete()}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? <RefreshCw className="animate-spin mr-2" size={14} /> : <Trash2 size={14} className="mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Bar */}
      {selectedContacts.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-5 py-3 rounded-full shadow-xl flex items-center gap-4 z-50" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedContacts.length} selected</span>
          <div className="h-4 w-px bg-slate-600" />
          <Button variant="ghost" size="sm" className="text-white hover:bg-slate-700 h-8" onClick={() => setSelectedContacts([])}>
            <X size={14} className="mr-1" /> Clear
          </Button>
          <Button variant="ghost" size="sm" className="text-blue-400 hover:bg-blue-900/50 h-8" onClick={() => setIsBulkOwnerDialogOpen(true)}>
            <UserCircle size={14} className="mr-1" /> Change Owner
          </Button>
          <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-900/50 h-8" onClick={() => setIsDeleteDialogOpen(true)}>
            <Trash2 size={14} className="mr-1" /> Delete
          </Button>
        </div>
      )}

      {/* Bulk Owner Dialog */}
      <Dialog open={isBulkOwnerDialogOpen} onOpenChange={setIsBulkOwnerDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="bulk-owner-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle className="h-5 w-5 text-slate-600" />
              Change Owner
            </DialogTitle>
            <DialogDescription>
              Assign {selectedContacts.length} contacts to a new owner.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-medium text-slate-700">Select New Owner</Label>
            <Select value={selectedNewOwner} onValueChange={setSelectedNewOwner}>
              <SelectTrigger className="mt-2" data-testid="new-owner-select">
                <SelectValue placeholder="Choose an owner..." />
              </SelectTrigger>
              <SelectContent>
                {allUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setIsBulkOwnerDialogOpen(false); setSelectedNewOwner(''); }}>
              Cancel
            </Button>
            <Button onClick={handleBulkOwnerChange} disabled={!selectedNewOwner || isBulkUpdating} className="bg-slate-900 hover:bg-slate-800">
              {isBulkUpdating ? 'Updating...' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
